import React, { Fragment, useState, useEffect, useRef } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button, Alert } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import { useLocation } from "react-router-dom";
import FilePreview from "../Common/FilePreview";
import SignatureModal from "../Common/SignatureModal";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";

const DeliveryList = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [deliveries, setDeliveries] = useState([]);
 const [selectedDelivery, setSelectedDelivery] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [approvingId, setApprovingId] = useState(null);
 const [showApproveModal, setShowApproveModal] = useState(false);
 const [searchParams] = useSearchParams();
 const location = useLocation();
 const query = new URLSearchParams(location.search);

 const projectId = query.get("project");
 const workOrderId = query.get("workOrder");
 const statusFromUrl = query.get("status");
 const [showDeleteModal, setShowDeleteModal] = useState(false);
 const [deleteId, setDeleteId] = useState(null);

 const [filters, setFilters] = usePersistentFilters("deliveries_filters", {
 search: "",
 status: statusFromUrl || "",
 fromDate: "",
 toDate: "",
 page: 1,
 limit: 10,
 });

 const [loading, setLoading] = useState(false);
 const [deleting, setDeleting] = useState(false);
 const [error, setError] = useState("");
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
 const canManageDeliveries =
 currentRole === "admin" ||
 Boolean(
 user?.permissions?.some((permission) =>
 ["create:deliveries", "edit:deliveries", "approve:deliveries", "reject:deliveries", "delete:deliveries"].includes(permission)
 )
 );

 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });

 const dataURLtoBlob = (dataURL) => {
 const arr = dataURL.split(",");
 const mime = arr[0].match(/:(.*?);/)[1];
 const bstr = atob(arr[1]);
 let n = bstr.length;
 const u8arr = new Uint8Array(n);
 while (n--) u8arr[n] = bstr.charCodeAt(n);
 return new Blob([u8arr], { type: mime });
 };

 // =========================
 // INITIAL FETCH
 // =========================
 useEffect(() => {
 fetchDeliveries();
 }, [
 projectId,
 workOrderId,
 filters.status,
 filters.page,
 filters.limit,
 filters.search,
 filters.fromDate,
 filters.toDate,
 ]);

 const fetchDeliveries = async () => {
 try {
 setLoading(true);
 setError("");
 
 const queryParams = new URLSearchParams();
 if (projectId) queryParams.append("project", projectId);
 if (workOrderId) queryParams.append("workOrder", workOrderId);
 if (filters.status) queryParams.append("status", filters.status);
 if (filters.search) queryParams.append("search", filters.search);
 if (filters.fromDate) queryParams.append("fromDate", filters.fromDate);
 if (filters.toDate) queryParams.append("toDate", filters.toDate);
 queryParams.append("page", filters.page);
 queryParams.append("limit", filters.limit);
 queryParams.append("sortBy", "createdAt");
 queryParams.append("sortOrder", "desc");

 const response = await api.get(`/deliveries?${queryParams.toString()}`);
 if (response.data.success) {
 setDeliveries(response.data.data || []);
 if (response.data.pagination) {
 setPagination(prev => ({
 ...prev,
 ...response.data.pagination
 }));
 }
 }
 } catch (error) {
 console.error("Error fetching deliveries:", error);
 setError(error.response?.data?.message || "Failed to fetch deliveries");
 } finally {
 setLoading(false);
 }
 };

 const handlePageChange = (page) => {
 setFilters(prev => ({ ...prev, page: page }));
 };

 // [OK] Helper to validate MongoDB ObjectId
 const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);
 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/deliveries/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `DELIVERY-${id}.pdf`;
 link.click();
 } catch (err) {
 swal("Error", "Failed to download PDF", "error");
 }
 };
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/deliveries/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" }),
 );

 window.open(fileURL, "_blank");
 } catch (err) {
 swal("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 // =========================
 // DELETE HANDLERS
 // =========================
 const handleDelete = (id) => {
 if (!isValidObjectId(id)) {
 swal("Error", "Invalid delivery ID", "error");
 return;
 }
 setDeleteId(id);
 setShowDeleteModal(true);
 };

 const confirmDelete = async () => {
 try {
 setDeleting(true);
 const response = await api.delete(`/deliveries/${deleteId}`);
 if (response.data.success) {
 setShowDeleteModal(false);
 setDeleteId(null);
 await fetchDeliveries();
 swal("Deleted", "Delivery deleted successfully", "success");
 }
 } catch (error) {
 console.error("Error deleting delivery:", error);
 const msg = error.response?.data?.message || "Failed to delete delivery";
 swal("Error", msg, "error");
 } finally {
 setDeleting(false);
 }
 };
 // =========================
 // APPROVE / REJECT
 // =========================
 const handleApproveDelivery = async (id) => {
 const confirm = await swal({
 title: "Approve Delivery?",
 text: "Signature is required for approval.",
 icon: "info",
 buttons: ["Cancel", "Provide Signature"],
 });

 if (!confirm) return;

 setApprovingId(id);
 setShowApproveModal(true);
 };

 const handleSaveSignature = async (file) => {
 try {
 setLoading(true);

 const formData = new FormData();
 formData.append("signature", file);

 const res = await api.put(`/deliveries/${approvingId}/approve`, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 setDeliveries((prev) =>
 prev.map((d) =>
 d._id === approvingId ? { ...d, status: "Accepted" } : d,
 ),
 );

 swal("Approved", "Delivery approved successfully", "success");
 setShowApproveModal(false);
 setApprovingId(null);
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to approve delivery",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 const handleRejectDelivery = async (id) => {
 const confirm = await swal({
 title: "Reject Delivery?",
 text: "This delivery will be rejected.",
 icon: "warning",
 buttons: ["Cancel", "Reject"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.put(`/deliveries/${id}/reject`);

 if (res.data.success) {
 setDeliveries((prev) =>
 prev.map((d) => (d._id === id ? { ...d, status: "Rejected" } : d)),
 );

 swal("Rejected", "Delivery rejected successfully", "success");
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to reject delivery",
 "error",
 );
 }
 };

 // =========================
 // VIEW HANDLERS
 // =========================
 const handleView = async (id) => {
 if (!isValidObjectId(id)) {
 swal("Error", "Invalid delivery ID", "error");
 return;
 }

 try {
 const response = await api.get(`/deliveries/${id}`);
 if (response.data.success) {
 setSelectedDelivery(response.data.data);
 setShowModal(true);
 }
 } catch (error) {
 console.error("Error fetching delivery details:", error);
 const msg =
 error.response?.data?.message || "Failed to load delivery details";
 swal("Error", msg, "error");
 }
 };

 const handleCloseModal = () => {
 setShowModal(false);
 setSelectedDelivery(null);
 };

 // =========================
 // HELPERS
 // =========================
 const formatDate = (dateString) => {
 if (!dateString) return "—";
 try {
 const date = new Date(dateString);
 return date.toLocaleDateString("en-IN", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 } catch {
 return "—";
 }
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case "Pending":
 return "warning";
 case "Received":
 return "info";
 case "Inspected":
 return "primary";
 case "Accepted":
 return "success";
 case "Rejected":
 return "danger";
 default:
 return "secondary";
 }
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </svg>
 );

 // =========================
 // RENDER
 // =========================
 return (
 <Fragment>
 <PageTitle activeMenu="Delivery" motherMenu="Logistics" />

 {error && (
 <Alert variant="danger" dismissible onClose={() => setError("")}>
 <i className="fas fa-exclamation-triangle me-2"></i>
 {error}
 </Alert>
 )}

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Delivery List</Card.Title>
 <PermissionWrapper user={user} required={["create:deliveries"]}>
 <Link
 to={
 projectId || workOrderId
 ? `/delivery/add?${projectId ? `project=${projectId}&` : ""}${
 workOrderId ? `workOrder=${workOrderId}` : ""
 }`
 : "/delivery/add"
 }
 className="btn btn-primary"
 >
 <i className="fas fa-plus me-2"></i>Add Delivery
 </Link>
 </PermissionWrapper>
 </Card.Header>

 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: "180px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search Delivery</label>
 <input
 type="text"
 className="form-control form-control-sm"
 placeholder="Search Delivery..."
 value={filters.search}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 search: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="d-flex flex-column" style={{ width: "140px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <select
 className="form-select form-select-sm"
 value={filters.status}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 status: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Statuses</option>
 <option value="Draft">Draft</option>
 <option value="Accepted">Accepted</option>
 <option value="Rejected">Rejected</option>
 <option value="Pending">Pending</option>
 </select>
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.fromDate}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 fromDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.toDate}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 toDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="ms-auto d-flex flex-column" style={{ width: "65px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <select
 className="form-select form-select-sm"
 value={filters.limit}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 limit: Number(e.target.value),
 page: 1,
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </select>
 </div>
 </div>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary" role="status">
 <span className="visually-hidden">Loading...</span>
 </div>
 <p className="mt-3">Loading deliveries...</p>
 </div>
 ) : deliveries.length === 0 ? (
 <div className="text-center py-5">
 <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
 <p className="text-muted">
 No deliveries found. Create one to get started!
 </p>
 <Link
 to={
 projectId || workOrderId
 ? `/delivery/add?${projectId ? `project=${projectId}&` : ""}${
 workOrderId ? `workOrder=${workOrderId}` : ""
 }`
 : "/delivery/add"
 }
 className="btn btn-primary"
 >
 <i className="fas fa-plus me-2"></i>Add Delivery
 </Link>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>#</th>
 <th>Delivery Number</th>
 <th>Work Order</th>

 <th>Delivery Date</th>
 <th>Status</th>
 <th className="text-center">Verified Copy</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>
 <tbody>
 {deliveries.map((item, i) => (
 <tr key={item._id}>
 <td>{(pagination.currentPage - 1) * pagination.limit + (i + 1)}</td>
 <td>
 <strong className="text-primary">
 {item.deliveryNumber || "—"}
 </strong>
 </td>
 <td style={{ fontSize: "0.9rem" }}>
 {item.workOrder
 ? item.workOrder.woNo || item.workOrder.woNumber || "—"
 : "—"}
 </td>
 <td className="text-nowrap">{formatDate(item.deliveryDate)}</td>
 <td>
 <Badge bg={getStatusBadge(item.status)}>
 {item.status || "Pending"}
 </Badge>
 </td>
 <td className="text-center">
 <VerifiedCopyActions
 id={item._id}
 type="deliveries"
 hasVerifiedCopy={!!item.verifiedCopy?.filePath}
 verifiedCopy={item.verifiedCopy}
 onUploadSuccess={fetchDeliveries}
 canUpload={canManageDeliveries}
 />
 </td>
 <td className="text-center">
 <Dropdown className="ms-auto">
 <Dropdown.Toggle
 variant=""
 as="div"
 className="btn-link i-false p-0 cursor-pointer"
 title="Click for more actions"
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 {/* ================= VIEW ================= */}
 <PermissionWrapper user={user} required={["read:deliveries"]}>
 <Dropdown.Item
 onClick={() => handleView(item._id)}
 title="View full delivery details"
 >
 <i className="fas fa-eye me-2 text-primary"></i>
 View Details
 </Dropdown.Item>
 </PermissionWrapper>

 {/* ================= EDIT ================= */}
 <PermissionWrapper user={user} required={["edit:deliveries"]}>
 <Dropdown.Item
 as={Link}
 to={
 projectId || workOrderId
 ? `/delivery/edit/${item._id}?${projectId ? `project=${projectId}&` : ""}${
 workOrderId
 ? `workOrder=${workOrderId}`
 : ""
 }`
 : `/delivery/edit/${item._id}`
 }
 disabled={item.status !== "Pending"}
 title="Edit delivery (only if Pending)"
 >
 <i className="fas fa-edit me-2 text-success"></i>
 Edit
 </Dropdown.Item>
 </PermissionWrapper>

 {/* ================= APPROVE / REJECT ================= */}
 {(item.status === "Pending" || item.status === "Received") && (
 <>
 <Dropdown.Divider />

 <PermissionWrapper user={user} required={["approve:deliveries"]}>
 <Dropdown.Item
 onClick={() =>
 handleApproveDelivery(item._id)
 }
 className="text-success"
 disabled={approvingId === item._id}
 >
 <i className="fas fa-check-circle me-2"></i>
 Approve
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["reject:deliveries"]}>
 <Dropdown.Item
 onClick={() =>
 handleRejectDelivery(item._id)
 }
 className="text-danger"
 >
 <i className="fas fa-times-circle me-2"></i>
 Reject
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}

 <Dropdown.Divider />

 <PermissionWrapper user={user} required={["read:deliveries"]}>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(item._id)}
 disabled={previewingPdf}
 title="Preview system-generated PDF"
 >
 <i className="fas fa-eye me-2 text-info"></i>
 Preview PDF
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() => handleDownloadPDF(item._id)}
 title="Download system-generated PDF"
 >
 <i className="fas fa-file-pdf me-2 text-danger"></i>
 Download PDF
 </Dropdown.Item>
 </PermissionWrapper>

 <Dropdown.Divider />

 {/* ================= DELETE ================= */}
 <PermissionWrapper user={user} required={["delete:deliveries"]}>
 <Dropdown.Item
 onClick={() => handleDelete(item._id)}
 className="text-danger"
 disabled={item.status !== "Pending"}
 >
 <i className="fas fa-trash me-2"></i>
 Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>

 {/* Pagination */}
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={deliveries.length}
 limit={pagination.limit}
 onPageChange={handlePageChange}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* =========================
 VIEW MODAL
 ========================== */}
 <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
 <Modal.Header closeButton>
 <Modal.Title>
 <i className="fas fa-truck me-2"></i>
 Delivery Details
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedDelivery && (
 <>
 {/* BASIC INFO */}
 <Card className="mb-3 border-0 premium-card">
 <Card.Body>
 <Row>
 <Col md={6}>
 <div className="mb-3">
 <small className="text-muted d-block">
 Delivery Number
 </small>
 <strong className="text-primary fs-5">
 {selectedDelivery.deliveryNumber || "—"}
 </strong>
 </div>
 </Col>

 <Col md={6}>
 <div className="mb-3">
 <small className="text-muted d-block">Work Order</small>
 <strong>
 {selectedDelivery.workOrder
 ? `${selectedDelivery.workOrder.woNo} — ${selectedDelivery.workOrder.scope || ""}`
 : "N/A"}
 </strong>
 </div>

 <div className="mb-3">
 <small className="text-muted d-block">Project</small>
 <strong>
 {selectedDelivery.project?.projectName || "N/A"}
 </strong>
 </div>

 <div className="mb-3">
 <small className="text-muted d-block">Purchase Order(s)</small>
 <div>
 {(() => {
 const pos = Array.isArray(selectedDelivery.purchaseOrder)
 ? selectedDelivery.purchaseOrder
 : selectedDelivery.purchaseOrder
 ? [selectedDelivery.purchaseOrder]
 : [];
 if (!pos.length) return <strong>N/A</strong>;
 return pos.map((po, idx) => (
 <Badge key={idx} bg="light" text="dark" className="border me-1 mb-1">
 <i className="fas fa-file-invoice me-1 text-primary"></i>
 {po.poNumber || po._id || po}
 </Badge>
 ));
 })()}
 </div>
 </div>

 <div className="mb-3">
 <small className="text-muted d-block">
 Delivery Date
 </small>
 <strong>
 {formatDate(selectedDelivery.deliveryDate)}
 </strong>
 </div>

 <div className="mb-3">
 <small className="text-muted d-block">Status</small>
 <Badge
 bg={getStatusBadge(selectedDelivery.status)}
 className="fs-6 rounded-pill"
 >
 {selectedDelivery.status || "Pending"}
 </Badge>
 </div>

 {selectedDelivery.receivedBy && (
 <div className="mb-3">
 <small className="text-muted d-block">
 Received By
 </small>
 <strong>
 {selectedDelivery.receivedBy.name ||
 selectedDelivery.receivedBy.email ||
 "N/A"}
 </strong>
 </div>
 )}
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* ITEMS TABLE */}
 {selectedDelivery.items && selectedDelivery.items.length > 0 && (
 <div className="mb-3">
 <h5 className="mb-3">
 <i className="fas fa-boxes me-2"></i>Items
 </h5>
 <div className="table-responsive">
 <Table bordered hover size="sm">
 <thead className="table-light">
 <tr>
 <th width="50">#</th>
 <th>Item Name</th>
 <th width="120">Qty Ordered</th>
 <th width="120">Qty Delivered</th>
 <th width="80">Unit</th>
 <th>Remark</th>
 </tr>
 </thead>
 <tbody>
 {selectedDelivery.items.map((item, index) => (
 <tr key={index}>
 <td>{index + 1}</td>
 <td>
 <div className="d-flex align-items-center gap-2">
 <strong>{item.itemName || "—"}</strong>
      {item.subItems && item.subItems.length > 0 && (
        <div className="mt-1 ps-2 border-start border-2 border-primary" style={{ fontSize: "0.78rem" }}>
          <div className="text-muted fw-bold">Sub-items:</div>
          {item.subItems.map((s, sIdx) => {
            const subDelQty = s.quantityDelivered !== undefined && s.quantityDelivered !== null && s.quantityDelivered !== ""
              ? s.quantityDelivered
              : (s.quantity !== undefined && s.quantity !== null && s.quantity !== "" ? s.quantity : 0);
            return (
              <div key={sIdx} className="text-secondary">
                • {s.name} — {subDelQty} {s.unit || item.unit || ""} {s.remarks ? `(${s.remarks})` : ""}
              </div>
            );
          })}
        </div>
      )}
 {item.poNumber && (
 <span className="badge bg-light text-primary border" style={{ fontSize: "0.7rem" }}>
 {item.poNumber}
 </span>
 )}
 </div>
 <div className="mt-1">
 {item.isNonBillable ? (
 <Badge
 bg="warning"
 text="dark"
 className="mt-1"
 style={{ fontSize: "0.7rem" }}
 >
 Non-Billable
 </Badge>
 ) : (
 <Badge bg="success" className="mt-1" style={{ fontSize: "0.7rem" }}>
 Billable
 </Badge>
 )}
 </div>
 </td>
 <td>{item.quantityOrdered ?? "—"}</td>
 <td className="text-primary">
 <strong>{item.quantityDelivered ?? "—"}</strong>
 </td>
 <td>{item.unit || "—"}</td>
 <td>{item.remark || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 </div>
 )}

 {/* DOCUMENTS */}
 <div className="mb-3">
 <h5 className="mb-3">
 <i className="fas fa-file-alt me-2"></i>Documents
 </h5>
 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Challan Document:</strong>
 {selectedDelivery.challanDocument?.fileUrl ? (
 <FilePreview 
 file={{
 fileName: selectedDelivery.challanDocument.fileName,
 fileUrl: selectedDelivery.challanDocument.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Document?",
 text: "This will remove the challan document.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/deliveries/${selectedDelivery._id}/remove-file`, { field: 'challanDocument' });
 if (res.data.success) {
 swal("Deleted!", "Challan removed", "success");
 handleView(selectedDelivery._id); 
 }
 } catch (err) {
 swal("Error", "Failed to remove document", "error");
 }
 }}
 />
 ) : (
 <div className="p-2 border rounded premium-bg text-muted mt-2">
 <small>No challan document uploaded</small>
 </div>
 )}
 </div>

 <div className="mb-3">
 <strong>Other related doc:</strong>
 {selectedDelivery.clientStamp?.fileUrl ? (
 <FilePreview 
 file={{
 fileName: selectedDelivery.clientStamp.fileName,
 fileUrl: selectedDelivery.clientStamp.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Document?",
 text: "This will remove the related document.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/deliveries/${selectedDelivery._id}/remove-file`, { field: 'clientStamp' });
 if (res.data.success) {
 swal("Deleted!", "Document removed", "success");
 handleView(selectedDelivery._id); 
 }
 } catch (err) {
 swal("Error", "Failed to remove document", "error");
 }
 }}
 />
 ) : (
 <div className="p-2 border rounded premium-bg text-muted mt-2">
 <small>No related document uploaded</small>
 </div>
 )}
 </div>
 </Col>

 <Col md={6}>
 <div className="mb-3">
 <strong>Test Certificate:</strong>
 {selectedDelivery.testCertificate?.fileUrl ? (
 <FilePreview 
 file={{
 fileName: selectedDelivery.testCertificate.fileName,
 fileUrl: selectedDelivery.testCertificate.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Certificate?",
 text: "This will remove the test certificate.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/deliveries/${selectedDelivery._id}/remove-file`, { field: 'testCertificate' });
 if (res.data.success) {
 swal("Deleted!", "Certificate removed", "success");
 handleView(selectedDelivery._id); 
 }
 } catch (err) {
 swal("Error", "Failed to remove certificate", "error");
 }
 }}
 />
 ) : (
 <div className="p-2 border rounded premium-bg text-muted mt-2">
 <small>No test certificate uploaded</small>
 </div>
 )}
 </div>

 <div className="mb-3">
 <strong>Signature:</strong>
 {selectedDelivery.signature?.fileUrl ? (
 <FilePreview 
 file={{
 fileName: selectedDelivery.signature.fileName || "Signature",
 fileUrl: selectedDelivery.signature.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Signature?",
 text: "This will remove the delivery signature.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/deliveries/${selectedDelivery._id}/remove-file`, { field: 'signature' });
 if (res.data.success) {
 swal("Deleted!", "Signature removed", "success");
 handleView(selectedDelivery._id); 
 }
 } catch (err) {
 swal("Error", "Failed to remove signature", "error");
 }
 }}
 />
 ) : (
 <div className="p-2 border rounded premium-bg text-muted mt-2">
 <small>No signature uploaded</small>
 </div>
 )}
 </div>
 </Col>
 </Row>
 </div>

 {/* DRIVER / DISPATCH DETAILS */}
 {(selectedDelivery.dispatchDetails?.driverName ||
 selectedDelivery.dispatchDetails?.driverPhone ||
 selectedDelivery.dispatchDetails?.vehicleNumber ||
 selectedDelivery.dispatchDetails?.transporterName) && (
 <div className="mb-3">
 <h5 className="mb-3">
 <i className="fas fa-truck-moving me-2"></i>
 Dispatch / Driver Details
 </h5>
 <Card className="border-0 premium-card">
 <Card.Body>
 <Row>
 <Col md={6}>
 <div className="mb-2">
 <small className="text-muted d-block">
 Driver Name
 </small>
 <strong>
 {selectedDelivery.dispatchDetails?.driverName ||
 "—"}
 </strong>
 </div>
 <div className="mb-2">
 <small className="text-muted d-block">
 Driver Phone
 </small>
 <strong>
 {selectedDelivery.dispatchDetails?.driverPhone ||
 "—"}
 </strong>
 </div>
 </Col>
 <Col md={6}>
 <div className="mb-2">
 <small className="text-muted d-block">
 Vehicle Number
 </small>
 <strong>
 {selectedDelivery.dispatchDetails
 ?.vehicleNumber || "—"}
 </strong>
 </div>
 <div className="mb-2">
 <small className="text-muted d-block">
 Transporter / Agency
 </small>
 <strong>
 {selectedDelivery.dispatchDetails
 ?.transporterName || "—"}
 </strong>
 </div>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 </div>
 )}

 {/* REMARKS */}
 {selectedDelivery.remarks && (
 <div className="mb-3">
 <h5 className="mb-3">
 <i className="fas fa-comment me-2"></i>Remarks
 </h5>
 <div className="p-3 border rounded premium-bg">
 <p className="mb-0">{selectedDelivery.remarks}</p>
 </div>
 </div>
 )}

 {/* TIMESTAMPS */}
 <div className="mt-4 pt-3 border-top">
 <Row>
 <Col md={6}>
 <small className="text-muted">
 <i className="fas fa-calendar-plus me-1"></i>
 Created:{" "}
 <strong>{formatDate(selectedDelivery.createdAt)}</strong>
 </small>
 </Col>
 <Col md={6} className="text-md-end">
 <small className="text-muted">
 <i className="fas fa-calendar-check me-1"></i>
 Last Updated:{" "}
 <strong>{formatDate(selectedDelivery.updatedAt)}</strong>
 </small>
 </Col>
 </Row>
 </div>
 </>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={handleCloseModal}>
 <i className="fas fa-times me-2"></i>Close
 </Button>
 {selectedDelivery && (
 <Button
 variant="primary"
 as={Link}
 to={
 projectId || workOrderId
 ? `/delivery/edit/${selectedDelivery._id}?${
 projectId ? `project=${projectId}&` : ""
 }${workOrderId ? `workOrder=${workOrderId}` : ""}`
 : `/delivery/edit/${selectedDelivery._id}`
 }
 onClick={handleCloseModal}
 >
 <i className="fas fa-edit me-2"></i>Edit Delivery
 </Button>
 )}
 </Modal.Footer>
 </Modal>



 {/* =========================
 DELETE CONFIRMATION MODAL
 ========================== */}
 <Modal
 show={showDeleteModal}
 onHide={() => !deleting && setShowDeleteModal(false)}
 centered
 >
 <Modal.Header closeButton={!deleting}>
 <Modal.Title>
 <i className="fas fa-exclamation-triangle text-danger me-2"></i>
 Confirm Delete
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <p>Are you sure you want to delete this delivery?</p>
 <p className="text-danger mb-0">
 <strong>This action cannot be undone!</strong>
 </p>
 </Modal.Body>
 <Modal.Footer>
 <Button
 variant="secondary"
 onClick={() => setShowDeleteModal(false)}
 disabled={deleting}
 >
 <i className="fas fa-times me-2"></i>Cancel
 </Button>
 <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
 {deleting ? (
 <>
 <span className="spinner-border spinner-border-sm me-2"></span>
 Deleting...
 </>
 ) : (
 <>
 <i className="fas fa-trash me-2"></i>Delete
 </>
 )}
 </Button>
 </Modal.Footer>
 </Modal>
 {/* =========================
 PDF PREVIEW LOADER
========================== */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing PDF preview…</h6>
 </div>
 </div>
 )}

 <SignatureModal
 show={showApproveModal}
 onHide={() => {
 setShowApproveModal(false);
 setApprovingId(null);
 }}
 onSave={handleSaveSignature}
 title="Client Signature for Delivery Approval"
 />
 </Fragment>
 );
};

export default DeliveryList;
